import { useVrBulkResolver } from "@components/virtual-receptionist/contacts/hooks/useBulkContactResolver";
import { useBulkResolveContacts } from "@components/virtual-receptionist/contacts/hooks/useBulkResolveContacts";
import { findResolvedByPhone, getCallerType, getDisplayName, getRoleLabel } from "@components/virtual-receptionist/contacts/types/bulk-resolver.types";
import CallEndIcon from "@mui/icons-material/CallEnd";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import SwapCallsIcon from "@mui/icons-material/SwapCalls";
import { Box, IconButton, Paper, Slide, Stack, Tooltip, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useFloatingBubbles } from "../../../context/floating-bubbles/FloatingBubblesContext";
import { playConfiguredCallSound, stopConfiguredCallSound } from "../../../notifications/utils/notificationSound";
import { useAppSelector } from "../../../redux/hooks/redux";
import { useOptionalInternalWebRTC } from "../../internal-contacts/_context/InternalWebRTCContext";
import { useOptionalInternalTwilioCall } from "../../internal-twilio-call/context/InternalTwilioCallContext";
import { alphaVar, colors } from "../../ui/colors";
import { useVirtualAssistant } from "../context/VirtualAssistantContext";
import { useContactCenterTheme } from "../theming/ContactCenterThemeContext";
import { ContactCenterThemeScope } from "../theming/ContactCenterThemeScope";
import { useOptionalCallArbitration } from "./arbitration/CallArbitrationContext";
import { InCallNotification } from "./in-call/InCallNotification";
import { IncomingCallNotification } from "./incoming-call/IncomingCallNotification";
import { VibrationType } from "./incoming-call/types/vibration.types";
import { getVibrationAnimation } from "./incoming-call/utils/getVibrationMode";

interface Props {
  vibrationMode?: VibrationType;
  vibrationSpeed?: number;
}

type VisibleCall =
  | {
    type: "incoming";
    key: string;
    companyId: string;
    remotePhone: { dialCode?: string | null; phone?: string | null } | null;
    callDuration: number;
    isMuted: boolean;
    isInternal?: boolean;
    internalCallerName?: string | null;
    internalCallerCompanyName?: string | null;
  }
  | {
    type: "in-call";
    key: string;
    companyId: string;
    remotePhone: { dialCode?: string | null; phone?: string | null } | null;
    callDuration: number;
    isMuted: boolean;
    isInternal?: boolean;
    internalCallerName?: string | null;
    internalCallerCompanyName?: string | null;
  }
  | null;

const getFullPhone = (dialCode?: string | null, phoneNumber?: string | null): string | null => {
  if (!phoneNumber) return null;

  const cleanPhone = phoneNumber.replace(/\s+/g, "").trim();
  const cleanDialCode = dialCode?.replace(/\s+/g, "").trim();

  if (cleanPhone.startsWith("+")) return cleanPhone;

  return cleanDialCode ? `${cleanDialCode}${cleanPhone}` : cleanPhone;
};

const getResolvedDisplayName = (resolved: any): string | null => {
  if (!resolved) return null;

  return getDisplayName(resolved) || resolved.displayName || resolved.fullName || resolved.name || null;
};

const getResolvedLabel = (resolved: any): string | null => {
  if (!resolved) return null;

  const label = getRoleLabel(resolved);

  if (label) return label;

  if (resolved.callerType === "client") return "Client";
  if (resolved.callerType === "workforce") return "Workforce";

  return null;
};

const getResolvedCallerType = (resolved: any): "client" | "workforce" | null => {
  if (!resolved) return null;

  if (resolved.callerType === "client") return "client";
  if (resolved.callerType === "workforce") return "workforce";

  return getCallerType(resolved);
};

const getCompanyIdFromCallKey = (key?: string | null): string | null => {
  if (!key) return null;

  const parts = key.split(":");

  // Format: sessionId:companyId:phoneNumber
  if (parts.length >= 3) return parts[1] || null;

  // Format: companyId:phoneNumber
  if (parts.length >= 2) return parts[0] || null;

  return null;
};

export function CallNotification({ vibrationMode = "vibrate", vibrationSpeed = 800 }: Props) {
  const { theme } = useContactCenterTheme();
  const vibrationStyle = useMemo(() => getVibrationAnimation(vibrationMode, vibrationSpeed), [vibrationMode, vibrationSpeed]);

  const [callOpen, setCallOpen] = useState(false);

  const { incomingCalls, companiesState, acceptCallByCompany, hangupCallByCompany, toggleMuteByCompany, openAddContactMenu } =
    useVirtualAssistant();

  const { openCalls, callsOpen, zIndex } = useFloatingBubbles();

  const currentCompany = useAppSelector((state) => state.userState.company);
  const companies = useAppSelector((state) => state.userState.companies) || [];

  const internalWebRTC = useOptionalInternalWebRTC();
  const internalTwilio = useOptionalInternalTwilioCall();
  const arbitration = useOptionalCallArbitration();

  const incomingCall = incomingCalls[0] || null;

  const ENABLE_IN_CALL_MOCK = false;
  const ENABLE_INCOMING_CALL_SOUND_MOCK = false;
  const mockCompanyId = String(currentCompany?.id ?? "106");

  const mockVisibleCall: VisibleCall = {
    type: ENABLE_INCOMING_CALL_SOUND_MOCK ? "incoming" : "in-call",
    key: "mock-key",
    companyId: mockCompanyId,
    remotePhone: {
      dialCode: "+1",
      phone: "2134747207",
    },
    callDuration: ENABLE_INCOMING_CALL_SOUND_MOCK ? 0 : 143,
    isMuted: false,
  };

  const visibleCall = useMemo<VisibleCall>(() => {
    if (ENABLE_IN_CALL_MOCK || ENABLE_INCOMING_CALL_SOUND_MOCK) return mockVisibleCall;

    if (arbitration?.alternating) {
      const webrtcLive = arbitration.heldSide === "pstn";

      if (webrtcLive && internalWebRTC) {
        return {
          type: "in-call",
          key: internalWebRTC.activeCall?.callUid ?? "internal",
          companyId: String(internalWebRTC.activeCall?.companyId ?? ""),
          remotePhone: null,
          callDuration: internalWebRTC.callDuration,
          isMuted: internalWebRTC.isMuted,
          isInternal: true,
        };
      }

      const pstnKey = arbitration.pstnActiveKey;
      const pstnState = pstnKey ? companiesState.get(pstnKey) : null;

      if (pstnKey && pstnState) {
        return {
          type: "in-call",
          key: pstnKey,
          companyId: String(pstnState.companyId || getCompanyIdFromCallKey(pstnKey) || ""),
          remotePhone: pstnState.remotePhone || null,
          callDuration: pstnState.callDuration || 0,
          isMuted: !!pstnState.isMuted,
        };
      }
    }

    if (internalTwilio?.internalCallView === "incoming") {
      return {
        type: "incoming",
        key: "internal-twilio",
        companyId: "",
        remotePhone: null,
        callDuration: 0,
        isMuted: false,
        isInternal: true,
      };
    }

    if (internalTwilio?.internalCallView === "in-call") {
      return {
        type: "in-call",
        key: "internal-twilio",
        companyId: "",
        remotePhone: null,
        callDuration: internalTwilio.callDuration,
        isMuted: internalTwilio.isMuted,
        isInternal: true,
      };
    }

    if (internalTwilio?.internalCallView === "outgoing-ringing") {
      return {
        type: "in-call",
        key: "internal-twilio-outgoing",
        companyId: "",
        remotePhone: null,
        callDuration: 0,
        isMuted: false,
        isInternal: true,
      };
    }

    if (internalWebRTC?.internalCallView === "incoming") {
      return {
        type: "incoming",
        key: internalWebRTC.incomingCall?.callUid ?? "internal",
        companyId: String(internalWebRTC.incomingCall?.companyId ?? ""),
        remotePhone: null,
        callDuration: 0,
        isMuted: false,
        isInternal: true,
      };
    }

    if (internalWebRTC?.internalCallView === "in-call") {
      return {
        type: "in-call",
        key: internalWebRTC.activeCall?.callUid ?? "internal",
        companyId: String(internalWebRTC.activeCall?.companyId ?? ""),
        remotePhone: null,
        callDuration: internalWebRTC.callDuration,
        isMuted: internalWebRTC.isMuted,
        isInternal: true,
      };
    }

    if (internalWebRTC?.internalCallView === "outgoing-ringing") {
      return {
        type: "in-call",
        key: internalWebRTC.activeCall?.callUid ?? "internal-outgoing",
        companyId: String(internalWebRTC.activeCall?.companyId ?? ""),
        remotePhone: null,
        callDuration: 0,
        isMuted: false,
        isInternal: true,
      };
    }

    if (incomingCall) {
      const companyId = incomingCall.companyId || getCompanyIdFromCallKey(incomingCall.key) || "";
      const isInternalIncoming = !!incomingCall.isInternalIncoming;

      return {
        type: "incoming",
        key: incomingCall.key,
        companyId: String(companyId),
        remotePhone: isInternalIncoming ? null : incomingCall.remotePhone || null,
        callDuration: 0,
        isMuted: false,
        isInternal: isInternalIncoming,
        internalCallerName: incomingCall.internalCallerName,
        internalCallerCompanyName: incomingCall.internalCallerCompanyName,
      };
    }

    const activeEntry = Array.from(companiesState.entries()).find(([_, item]) => item.activeCall && item.callDirection !== "INCOMING");

    if (!activeEntry) return null;

    const [key, lastActive] = activeEntry;

    const companyId = lastActive.companyId || getCompanyIdFromCallKey(key) || "";
    const isInternalActive = !!lastActive.isInternalIncoming;

    return {
      type: "in-call",
      key,
      companyId: String(companyId),
      remotePhone: isInternalActive ? null : lastActive.remotePhone || null,
      callDuration: lastActive.callDuration || 0,
      isMuted: !!lastActive.isMuted,
      isInternal: isInternalActive,
      internalCallerName: lastActive.internalCallerName,
      internalCallerCompanyName: lastActive.internalCallerCompanyName,
    };
  }, [incomingCall, companiesState, internalWebRTC, internalTwilio, arbitration, mockCompanyId]);

  const visibleCompanyName = useMemo(() => {
    if (ENABLE_IN_CALL_MOCK) {
      return "EMCB Group";
    }

    if (!visibleCall?.companyId) return undefined;

    if (String(currentCompany?.id) === String(visibleCall.companyId)) {
      return currentCompany?.businessName;
    }

    const matchedCompany = companies.find(
      (item: any) => String(item.companyId) === String(visibleCall.companyId) || String(item.company?.id) === String(visibleCall.companyId),
    );

    return matchedCompany?.company?.businessName;
  }, [visibleCall?.companyId, currentCompany, companies]);

  const visiblePhoneNumber = visibleCall?.remotePhone?.phone;
  const visibleDialCode = visibleCall?.remotePhone?.dialCode;
  const visibleFullPhone = getFullPhone(visibleDialCode, visiblePhoneNumber);

  const {
    data: legacyResolvedMap = {},
    isLoading: isLoadingLegacyResolved,
    error: legacyResolverError,
  } = useBulkResolveContacts(!ENABLE_IN_CALL_MOCK && visibleFullPhone ? [visibleFullPhone] : []);

  const legacyResolved = !ENABLE_IN_CALL_MOCK && visibleFullPhone ? findResolvedByPhone(legacyResolvedMap, visibleFullPhone) : null;

  const shouldResolveFromVrContacts = !ENABLE_IN_CALL_MOCK && !!visibleFullPhone && !!visibleCall?.companyId && !visibleCall?.isInternal;

  const {
    data: vrResolvedMap = {},
    isLoading: isLoadingVrResolved,
    error: vrResolverError,
  } = useVrBulkResolver(visibleCall?.companyId, shouldResolveFromVrContacts ? [visibleFullPhone] : [], shouldResolveFromVrContacts);

  const vrResolved = !ENABLE_IN_CALL_MOCK && visibleFullPhone ? findResolvedByPhone(vrResolvedMap, visibleFullPhone) : null;

  const logicalResolvedContact = legacyResolved || vrResolved || null;
  const displayResolvedContact = vrResolved || legacyResolved || null;

  const visibleDisplayName = getResolvedDisplayName(displayResolvedContact);
  const visibleCallerType = getResolvedCallerType(logicalResolvedContact);
  const visibleCallerLabel = getResolvedLabel(displayResolvedContact);

  const callerInfo = useMemo(() => {
    if (ENABLE_IN_CALL_MOCK) {
      return {
        displayName: "John Doe",
        phoneNumber: "2134747207",
        dialCode: "+1",
        callerType: "client",
        callerLabel: "Client",
        receivingCompanyName: "EMCB Group",
      };
    }

    if (visibleCall?.isInternal && internalTwilio?.internalCallView) {
      return {
        displayName: internalTwilio.peerDisplayName,
        phoneNumber: undefined,
        dialCode: undefined,
        callerType: "workforce",
        callerLabel: "Internal",
        receivingCompanyName: undefined,
      };
    }

    if (visibleCall?.isInternal && internalWebRTC?.internalCallView) {
      return {
        displayName: internalWebRTC.peerDisplayName,
        phoneNumber: internalWebRTC.peerSubtitle,
        dialCode: undefined,
        callerType: "workforce",
        callerLabel: "Workforce",
        receivingCompanyName: internalWebRTC.internalCallView === "incoming" ? internalWebRTC.localCompanyName || visibleCompanyName : undefined,
      };
    }

    if (visibleCall?.isInternal) {
      return {
        displayName: visibleCall.internalCallerName || "Internal contact",
        phoneNumber: undefined,
        dialCode: undefined,
        callerType: "workforce",
        callerLabel: "Internal",
        receivingCompanyName: visibleCall.internalCallerCompanyName || visibleCompanyName,
      };
    }

    const phoneNumber = visibleCall?.remotePhone?.phone;
    const dialCode = visibleCall?.remotePhone?.dialCode;

    return {
      displayName: visibleDisplayName || "Unknown",
      phoneNumber,
      dialCode,
      callerType: visibleCallerType || undefined,
      callerLabel: visibleCallerLabel || undefined,
      receivingCompanyName: visibleCompanyName,
    };
  }, [
    visibleCall,
    visibleDisplayName,
    visibleCallerType,
    visibleCallerLabel,
    visibleCompanyName,
    internalWebRTC,
    internalTwilio,
  ]);

  const shouldShowNotification = !!visibleCall && !callsOpen && !arbitration?.showChoice;
  const shouldVibrate = visibleCall?.type === "incoming" && !callsOpen && !arbitration?.showChoice;

  const hasActiveCall = useMemo(() => {
    const pstnInCall = Array.from(companiesState.values()).some((state) => state.activeCall && state.callDirection !== "INCOMING");
    const internalInCall =
      internalWebRTC?.internalCallView === "in-call" ||
      internalTwilio?.internalCallView === "in-call" ||
      internalTwilio?.internalCallView === "outgoing-ringing";

    return pstnInCall || internalInCall;
  }, [companiesState, internalWebRTC?.internalCallView, internalTwilio?.internalCallView]);

  const answerLabel = hasActiveCall ? "Answer and hold current." : "Answer";

  const alternatingHeldLabel = arbitration?.alternating
    ? arbitration.heldSide === "pstn"
      ? arbitration.pstnLabel
      : arbitration.webrtcLabel
    : null;

  const hangupHeldCall = () => {
    if (!arbitration) return;
    if (arbitration.heldSide === "pstn") {
      if (arbitration.pstnActiveKey) hangupCallByCompany(arbitration.pstnActiveKey);
    } else {
      void internalWebRTC?.hangup();
    }
  };

  useEffect(() => {
    setCallOpen(shouldShowNotification);
  }, [shouldShowNotification]);

  useEffect(() => {
    if (visibleCall?.type !== "incoming") {
      stopConfiguredCallSound();
      return;
    }

    playConfiguredCallSound(visibleCall.companyId);

    return () => {
      stopConfiguredCallSound();
    };
  }, [visibleCall?.companyId, visibleCall?.key, visibleCall?.type]);

  return (
    <ContactCenterThemeScope>
      <Slide direction="down" in={callOpen} mountOnEnter unmountOnExit>
        <Paper
          elevation={0}
          sx={{
            position: "fixed",
            top: 92,
            right: 20,
            zIndex: zIndex.callNotification,
            width: 300,
            background: alphaVar(theme.toast.bg, 0.95),
            color: theme.shell.textPrimary,
            backdropFilter: "blur(10px)",
            padding: "16px 16px 12px",
            borderRadius: 2,
            boxShadow: `0 4px 16px ${alphaVar("#000000", 0.14)}`,
            border: `1px solid ${theme.shell.border}`,
            ...(shouldVibrate ? vibrationStyle : {}),
          }}
        >
          {arbitration?.alternating && (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              onClick={arbitration.switchActive}
              sx={{
                cursor: "pointer",
                px: 1,
                py: 0.75,
                mb: 1,
                borderRadius: 1.5,
                border: `1px solid ${theme.shell.border}`,
                backgroundColor: alphaVar(theme.shell.accent, 0.06),
                transition: "background-color 0.15s ease",
                "&:hover": { backgroundColor: alphaVar(theme.shell.accent, 0.12) },
              }}
            >
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                <PauseCircleOutlineIcon sx={{ fontSize: "1rem", color: colors.warning.base }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography noWrap sx={{ fontSize: "0.78rem", fontWeight: 600, color: theme.shell.textPrimary }}>
                    {alternatingHeldLabel}
                  </Typography>
                  <Typography sx={{ fontSize: "0.64rem", color: theme.shell.textSecondary }}>
                    On hold · tap to switch
                  </Typography>
                </Box>
              </Stack>

              <Stack direction="row" spacing={0.25} alignItems="center">
                <Tooltip title="Switch to this call">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      arbitration.switchActive();
                    }}
                    sx={{ color: theme.shell.accent, "&:hover": { backgroundColor: alphaVar(theme.shell.accent, 0.14) } }}
                  >
                    <SwapCallsIcon sx={{ fontSize: "1.05rem" }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="End call on hold">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      hangupHeldCall();
                    }}
                    sx={{ color: colors.danger.base, "&:hover": { backgroundColor: alphaVar(colors.danger.base, 0.1) } }}
                  >
                    <CallEndIcon sx={{ fontSize: "0.95rem" }} />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          )}

          {visibleCall?.type === "incoming" && (
            <IncomingCallNotification
              callerInfo={callerInfo}
              answerLabel={answerLabel}
              onAnswer={() => {
                if (visibleCall.isInternal && internalTwilio?.internalCallView === "incoming") {
                  internalTwilio.acceptCall();
                  openCalls();
                  return;
                }

                if (visibleCall.isInternal && internalWebRTC?.internalCallView === "incoming") {
                  void internalWebRTC.acceptIncoming();
                  openCalls();
                  return;
                }

                acceptCallByCompany(visibleCall.key);
                openCalls();
              }}
              onReject={() => {
                if (visibleCall.isInternal && internalTwilio?.internalCallView === "incoming") {
                  internalTwilio.rejectCall();
                  return;
                }

                if (visibleCall.isInternal && internalWebRTC?.internalCallView === "incoming") {
                  void internalWebRTC.rejectIncoming();
                  return;
                }

                hangupCallByCompany(visibleCall.key);
              }}
            />
          )}

          {visibleCall?.type === "in-call" && (
            <InCallNotification
              companyId={visibleCall.companyId}
              transferCall={() => { }}
              callerInfo={callerInfo}
              callDuration={visibleCall.callDuration}
              isMuted={visibleCall.isMuted}
              isInternal={!!visibleCall.isInternal}
              contactPhoneNumber={visibleFullPhone}
              onQuickSave={openAddContactMenu}
              hangupCall={() => {
                if (ENABLE_IN_CALL_MOCK) {
                  console.log("Mock hangup");
                  return;
                }

                if (visibleCall.isInternal && internalTwilio?.internalCallView) {
                  internalTwilio.hangupCall();
                  return;
                }

                if (visibleCall.isInternal && internalWebRTC?.internalCallView) {
                  if (internalWebRTC.internalCallView === "outgoing-ringing") {
                    void internalWebRTC.cancelOutgoing();
                    return;
                  }

                  void internalWebRTC.hangup();
                  return;
                }

                hangupCallByCompany(visibleCall.key);
              }}
              toggleMute={() => {
                if (ENABLE_IN_CALL_MOCK) {
                  console.log("Mock toggle mute");
                  return;
                }

                if (visibleCall.isInternal && internalTwilio?.internalCallView === "in-call") {
                  internalTwilio.toggleMute();
                  return;
                }

                if (visibleCall.isInternal && internalWebRTC?.internalCallView) {
                  if (internalWebRTC.internalCallView === "outgoing-ringing") {
                    return;
                  }

                  internalWebRTC.toggleMute();
                  return;
                }

                toggleMuteByCompany(visibleCall.key);
              }}
            />
          )}
        </Paper>
      </Slide>
    </ContactCenterThemeScope>
  );
}
